import JSZip from 'jszip';
import { basename, buildImportInfo, findByBasename } from './sd-card-entries';
import { SdCardError, type SdCardEntry } from './types';
import { finalize, md5Base64, type PackProgress } from './pack-common';

// P3.2 / prisma VENT → .pvdat.
// Each NNNN_YYYY-MM-DD.zip session archive is expanded under <date>/<seq>/, and the
// latest session's device.xml + statistic.proto are lifted to the root alongside a
// derived Hash.txt (see P3_2ImporterBase).

const SESSION_ZIP_NAME_RE = /^(\d{4})_(\d{4}-\d{2}-\d{2})\.zip$/i;

export const packP32 = async (
  entries: SdCardEntry[],
  onProgress?: PackProgress,
): Promise<Blob> => {
  const sessions = entries
    .map((entry) => {
      const match = basename(entry.path).match(SESSION_ZIP_NAME_RE);
      return match
        ? { entry, seq: Number.parseInt(match[1], 10), date: match[2] }
        : null;
    })
    .filter((session): session is NonNullable<typeof session> => session !== null)
    .sort((a, b) => (a.date === b.date ? a.seq - b.seq : a.date.localeCompare(b.date)));

  if (sessions.length === 0) {
    throw new SdCardError(
      'No prisma VENT session archives (NNNN_YYYY-MM-DD.zip) were found on the card.',
    );
  }

  const zip = new JSZip();
  let latestInner: JSZip | null = null;
  let latestSeq = 0;
  let addedFileCount = 0;

  for (let i = 0; i < sessions.length; i += 1) {
    const session = sessions[i];
    let inner: JSZip;
    try {
      inner = await JSZip.loadAsync(session.entry.file);
    } catch {
      // Skip an unreadable session archive rather than failing the whole card.
      continue;
    }

    const folder = `${session.date}/${session.seq}`;
    for (const innerEntry of Object.values(inner.files)) {
      if (innerEntry.dir) continue;
      const blob = await innerEntry.async('blob');
      zip.file(`${folder}/${innerEntry.name}`, blob);
      addedFileCount += 1;
    }
    latestInner = inner;
    latestSeq = session.seq;
    onProgress?.((i / sessions.length) * 60);
  }

  // If every session archive failed to load we'd otherwise emit an archive with
  // no recordings, which fails later with a confusing generic upload error.
  if (addedFileCount === 0) {
    throw new SdCardError(
      'None of the prisma VENT session archives on the card could be read.',
    );
  }

  // Therapy-settings parsing (PvdatReader) resolves the device key from a
  // root-level device.xml.
  const rootDevice = findByBasename(entries, 'device.xml');
  if (rootDevice) {
    zip.file('device.xml', rootDevice.file);
  } else if (latestInner) {
    const innerDevice = Object.values(latestInner.files).find(
      (entry) => !entry.dir && basename(entry.name).toLowerCase() === 'device.xml',
    );
    if (innerDevice) zip.file('device.xml', await innerDevice.async('blob'));
  }

  // pTS copies the latest session's statistic.proto to the archive root and writes
  // Hash.txt = <seq>_base64(MD5(statistic.proto)) (P3_2ImporterBase). Its
  // P3_2ContainerFileImporter requires both, and device detection keys on a *root*
  // statistic.proto — the per-session copies under <date>/<seq>/ don't count.
  if (latestInner) {
    const innerStatistic = Object.values(latestInner.files).find(
      (entry) =>
        !entry.dir && basename(entry.name).toLowerCase() === 'statistic.proto',
    );
    if (innerStatistic) {
      const statistic = await innerStatistic.async('arraybuffer');
      zip.file('statistic.proto', statistic);
      zip.file('Hash.txt', `${latestSeq}_${md5Base64(statistic)}`);
    }
  }

  zip.file('importInfo.txt', buildImportInfo('P3.2'));

  return finalize(zip, (percent) => onProgress?.(60 + percent * 0.4));
};
